import React, {useContext, useState} from "react";
import {
    Alert,
    Avatar,
    Box,
    Button,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import {useMutation, useQuery} from "react-query";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";

import PageHeader from "../../../components/PageHeader";
import SectionCard from "../../../components/SectionCard";
import StatusBadge from "../../../components/StatusBadge";
import Spinner from "../../../components/Spinner";
import CustomTabPanel from "../../../components/CustomTabPanel";
import {changePassword, fetchUserInfo} from "../../../api/userApi";
import {LanguageContext} from "../../../components/LanguageContext";

const a11yProps = (index) => ({
    id: `profile-tab-${index}`,
    "aria-controls": `profile-tabpanel-${index}`,
});

const PasswordField = ({name, label}) => {
    const [show, setShow] = useState(false);
    return (
        <FormControl fullWidth variant="outlined">
            <InputLabel htmlFor={`pw-${name}`}>{label}</InputLabel>
            <OutlinedInput
                id={`pw-${name}`}
                name={name}
                type={show ? "text" : "password"}
                label={label}
                required
                endAdornment={
                    <InputAdornment position="end">
                        <IconButton
                            onClick={() => setShow((v) => !v)}
                            onMouseDown={(e) => e.preventDefault()}
                            edge="end"
                        >
                            {show ? <VisibilityOff/> : <Visibility/>}
                        </IconButton>
                    </InputAdornment>
                }
            />
        </FormControl>
    );
};

const roleLabel = (user, translations) => {
    const t = (user?.type || "").toUpperCase();
    if (t === "O") return translations?.roleOwner || "Owner";
    if (t === "A") return translations?.roleAdmin || "Administrator";
    return translations?.roleUser || "Korisnik";
};

const roleStatus = (user) =>
    (user?.type || "").toUpperCase() === "O" ? "info" : "neutral";

const UserProfile = () => {
    const {translations} = useContext(LanguageContext) || {};

    const [tab, setTab] = useState(0);
    const [validationError, setValidationError] = useState("");
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");

    const {data, isLoading, isError} = useQuery({
        queryKey: ["userId"],
        queryFn: () => fetchUserInfo(),
    });

    const user = data?.data;

    const mutation = useMutation({
        mutationFn: (formData) => changePassword(formData),
        onSuccess: (resp) => {
            setError("");
            setSuccess(resp.data.message);
        },
        onError: (err) => {
            setSuccess("");
            setError(err.response?.data?.message || "");
        },
    });

    const handleChangePassword = (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const oldP = formData.get("old_password");
        const newP = formData.get("new_password");
        const confirmP = formData.get("confirm_password");

        if (!oldP || !newP || !confirmP) {
            setValidationError(
                translations?.adminUserProfileAllFieldsRequired ||
                "Sva polja su obavezna!"
            );
            return;
        }
        if (newP !== confirmP) {
            setValidationError(
                translations?.adminUserProfilePasswordMismatch ||
                "Nova lozinka i potvrda se ne poklapaju!"
            );
            return;
        }
        setValidationError("");
        formData.append("id_user", localStorage.getItem("user_id"));
        mutation.mutateAsync(formData).catch(() => {
        });
    };

    if (isLoading) return <Spinner variant="inline"/>;
    if (isError) {
        return (
            <Alert severity="error">
                {translations?.adminUserProfileFetchError ||
                    "Greška prilikom preuzimanja podataka!"}
            </Alert>
        );
    }

    return (
        <Box>
            <PageHeader
                icon={<PersonOutlineRoundedIcon/>}
                eyebrow={translations?.adminProfile || "Profil"}
                title={`${user?.name || ""} ${user?.surname || ""}`.trim()}
                description={user?.email}
            />

            <Stack spacing={3}>
                <SectionCard>
                    <Stack
                        direction={{xs: "column", md: "row"}}
                        spacing={3}
                        alignItems="center"
                    >
                        <Avatar
                            sx={{
                                width: 96,
                                height: 96,
                                bgcolor: "primary.main",
                                color: "primary.contrastText",
                                fontSize: "2rem",
                            }}
                        >
                            {(user?.name || "?")[0]?.toUpperCase()}
                        </Avatar>
                        <Box sx={{flex: 1, minWidth: 0}}>
                            <Stack direction="row" spacing={1} sx={{mb: 1}}>
                                <StatusBadge
                                    status={roleStatus(user)}
                                    showDot={false}
                                    label={roleLabel(user, translations)}
                                />
                                <StatusBadge
                                    status={
                                        Number(user?.active) === 1
                                            ? "active"
                                            : "inactive"
                                    }
                                    label={
                                        Number(user?.active) === 1
                                            ? translations?.activeStore ||
                                            "Aktivan"
                                            : translations?.disabledStore ||
                                            "Neaktivan"
                                    }
                                />
                            </Stack>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography
                                        variant="overline"
                                        color="text.secondary"
                                    >
                                        {translations?.adminUserProfileName ||
                                            "Ime"}
                                    </Typography>
                                    <Typography sx={{fontWeight: 600}}>
                                        {user?.name}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography
                                        variant="overline"
                                        color="text.secondary"
                                    >
                                        {translations?.adminUserProfileSurname ||
                                            "Prezime"}
                                    </Typography>
                                    <Typography sx={{fontWeight: 600}}>
                                        {user?.surname}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography
                                        variant="overline"
                                        color="text.secondary"
                                    >
                                        {translations?.adminUserProfileEmail ||
                                            "Email"}
                                    </Typography>
                                    <Typography sx={{fontWeight: 600}}>
                                        {user?.email}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Stack>
                </SectionCard>

                <SectionCard padded={false}>
                    <Tabs
                        value={tab}
                        onChange={(e, v) => setTab(v)}
                        sx={(theme) => ({
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            px: 2,
                        })}
                    >
                        <Tab
                            icon={<LockOutlinedIcon fontSize="small"/>}
                            iconPosition="start"
                            label={
                                translations?.adminUserProfileChangePassword ||
                                "Promena lozinke"
                            }
                            sx={{
                                textTransform: "none",
                                minHeight: 56,
                                fontWeight: 500,
                            }}
                            {...a11yProps(0)}
                        />
                        <Tab
                            icon={
                                <SupportAgentOutlinedIcon fontSize="small"/>
                            }
                            iconPosition="start"
                            label={
                                translations?.adminUserProfileSupport ||
                                "Podrška"
                            }
                            sx={{
                                textTransform: "none",
                                minHeight: 56,
                                fontWeight: 500,
                            }}
                            {...a11yProps(1)}
                        />
                    </Tabs>

                    <Box sx={{p: 3}}>
                        <CustomTabPanel value={tab} index={0}>
                            <Stack
                                component="form"
                                onSubmit={handleChangePassword}
                                spacing={2}
                                noValidate
                            >
                                {validationError && (
                                    <Alert
                                        severity="warning"
                                        onClose={() => setValidationError("")}
                                    >
                                        {validationError}
                                    </Alert>
                                )}
                                {error && (
                                    <Alert
                                        severity="error"
                                        onClose={() => setError("")}
                                    >
                                        {error}
                                    </Alert>
                                )}
                                {success && (
                                    <Alert
                                        severity="success"
                                        onClose={() => setSuccess("")}
                                    >
                                        {success}
                                    </Alert>
                                )}
                                <PasswordField
                                    name="old_password"
                                    label={
                                        translations?.adminUserProfileOldPassword ||
                                        "Stara lozinka"
                                    }
                                />
                                <PasswordField
                                    name="new_password"
                                    label={
                                        translations?.adminUserProfileNewPassword ||
                                        "Nova lozinka"
                                    }
                                />
                                <PasswordField
                                    name="confirm_password"
                                    label={
                                        translations?.adminUserProfileConfirmNewPassword ||
                                        "Potvrdite novu lozinku"
                                    }
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    sx={{mt: 1, alignSelf: "flex-start"}}
                                    disabled={mutation.isLoading}
                                >
                                    {translations?.adminUserProfileChangePassword ||
                                        "Promeni lozinku"}
                                </Button>
                            </Stack>
                        </CustomTabPanel>
                        <CustomTabPanel value={tab} index={1}>
                            <Stack
                                component="form"
                                spacing={2}
                                noValidate
                            >
                                <TextField
                                    multiline
                                    rows={5}
                                    fullWidth
                                    label={
                                        translations?.adminUserProfileMessage ||
                                        "Poruka"
                                    }
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    sx={{alignSelf: "flex-start"}}
                                >
                                    {translations?.adminUserProfileSendMessage ||
                                        "Pošalji poruku"}
                                </Button>
                            </Stack>
                        </CustomTabPanel>
                    </Box>
                </SectionCard>
            </Stack>
        </Box>
    );
};

export default UserProfile;